/**
 * DM W5-6 (K11) — a `dm.plan`'s `data.slices[]`: the work items its approval materializes under the plan's parent.
 * `PM_DATA_FIELD_TYPES` is scalar-only, so `data_schema` cannot state this array; this zod schema is the contract
 * (the `dmJobDataSchema` precedent) and the kernel checks it on every dm.plan write.
 */
import { z } from 'zod';
/** The plan's data key holding the list. */
export const DM_PLAN_SLICES_FIELD = 'slices';
export const DM_PLAN_SLICES_MAX = 60;
/** The materialized dm.item's back-reference: the slice `key` it was created for. */
export const DM_PLAN_SLICE_KEY_FIELD = 'plan_slice_key';
/** …and the plan it was created by (itemId): a plan diffs only its own slices. */
export const DM_PLAN_SLICE_OF_FIELD = 'plan_slice_of';
export const DM_PLAN_SLICE_KINDS = ['slice', 'wave'];
/** D3 — a revision dropping a slice past `planned` (evidence) is refused under this gate. */
export const DM_PLAN_SLICE_EVIDENCE_GATE = 'plan-slice-evidence';
/** D4 — an approval touching slices outside one wave needs a human caller. */
export const DM_PLAN_SLICE_SCOPE_GATE = 'plan-slice-scope';
/** One plan governs a parent's slices: a second approved plan with slices there is refused. */
export const DM_PLAN_SLICE_CONFLICT_GATE = 'plan-slice-conflict';
/** An abandoned slice is terminal: a plan listing its key again is refused (new work, new key). */
export const DM_PLAN_SLICE_KEY_REUSED_GATE = 'plan-slice-key-reused';
/** A listed key names another plan's live slice under the parent that a successor cannot adopt (W6-H: only a superseded plan's, one per key): refused. */
export const DM_PLAN_SLICE_KEY_CONFLICT_GATE = 'plan-slice-key-conflict';
/** W5-7b — a slice the containment matrix would refuse under the plan's parent: refused before any create. */
export const DM_PLAN_SLICE_PLACEMENT_GATE = 'plan-slice-placement';
export const DM_PLAN_SLICE_KEY_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const dmPlanSliceSchema = z
    .object({
    key: z.string().min(1).max(64).regex(DM_PLAN_SLICE_KEY_PATTERN, 'a slice key is a slug (^[a-z0-9][a-z0-9-]*$)'),
    title: z.string().trim().min(1).max(500),
    kind: z.enum(DM_PLAN_SLICE_KINDS),
    summary: z.string().trim().min(1).max(500).optional(),
    /** Plan order: ascending; the materialized sort_order is the slice's index in that order. */
    order: z.number().int().min(0),
})
    .strict();
function duplicatesOf(values) {
    const seen = new Set();
    const dup = new Set();
    for (const value of values)
        (seen.has(value) ? dup : seen).add(value);
    return [...dup];
}
export const dmPlanSlicesSchema = z
    .array(dmPlanSliceSchema)
    .max(DM_PLAN_SLICES_MAX)
    .superRefine((slices, ctx) => {
    const keys = duplicatesOf(slices.map((s) => s.key));
    if (keys.length > 0)
        ctx.addIssue({ code: 'custom', message: `slice keys must be unique: ${keys.join(', ')}` });
    const orders = duplicatesOf(slices.map((s) => s.order));
    if (orders.length > 0)
        ctx.addIssue({ code: 'custom', message: `slice orders must be unique: ${orders.join(', ')}` });
});
/** A validated list in plan order (ascending `order`); index = the materialized sort_order. */
export function dmPlanSlicesInOrder(slices) {
    return [...slices].sort((a, b) => a.order - b.order);
}
//# sourceMappingURL=dmPlanSlices.js.map